import type { LayoutClassName } from "@replo/design-system/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { cva } from "class-variance-authority";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { useCarouselScroll } from "./useCarouselScroll";

const carouselVariants = cva(
  "flex overflow-x-auto overflow-y-hidden scroll-smooth",
  {
    variants: {
      gap: {
        "2": "gap-2",
        "3": "gap-3",
        "4": "gap-4",
      },
      snap: {
        true: "snap-x snap-mandatory",
        false: "",
      },
      hideScrollbar: {
        true: "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
        false: "",
      },
    },
    defaultVariants: {
      gap: "3",
      snap: true,
      hideScrollbar: true,
    },
  },
);

const navButtonVariants = cva(
  "absolute top-1/2 -translate-y-1/2 shadow-md z-10 hidden md:flex",
  {
    variants: {
      navButtonOffset: {
        "4": "",
        "8": "",
      },
      position: {
        left: "",
        right: "",
      },
    },
    compoundVariants: [
      { navButtonOffset: "4", position: "left", className: "-left-4" },
      { navButtonOffset: "4", position: "right", className: "-right-4" },
      { navButtonOffset: "8", position: "left", className: "-left-8" },
      { navButtonOffset: "8", position: "right", className: "-right-8" },
    ],
    defaultVariants: {
      navButtonOffset: "8",
    },
  },
);

type HorizontalCarouselProps<ClassName extends string = string> =
  React.PropsWithChildren<
    VariantProps<typeof carouselVariants> & {
      /**
       * className with type safety for layout classes only
       */
      className?: LayoutClassName<ClassName>;
      /**
       * flexible className marked as UNSAFE to indicate it is circumventing the design system
       */
      UNSAFE_className?: string;
      /**
       * Color for the gradient fade overlays. Defaults to "white".
       * Use CSS color values like "white", "#fff", "rgb(255,255,255)".
       */
      gradientColor?: string;
      /**
       * Delay in ms before attaching scroll listeners.
       * Useful when items animate in to prevent nav button flashing.
       */
      listenerDelay?: number;
      /**
       * Position offset for navigation buttons from container edge.
       */
      navButtonOffset?: "4" | "8";
      /**
       * Callback when scroll state changes.
       */
      onScrollChange?: (state: {
        canScrollLeft: boolean;
        canScrollRight: boolean;
      }) => void;
    }
  >;

function HorizontalCarouselInner<ClassName extends string>(
  {
    children,
    className,
    UNSAFE_className,
    gradientColor = "white",
    listenerDelay = 0,
    gap = "3",
    snap = true,
    hideScrollbar = true,
    navButtonOffset = "8",
    onScrollChange,
  }: HorizontalCarouselProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const { scrollContainerRef, canScrollLeft, canScrollRight, scroll } =
    useCarouselScroll({ listenerDelay });

  React.useEffect(() => {
    onScrollChange?.({ canScrollLeft, canScrollRight });
  }, [canScrollLeft, canScrollRight, onScrollChange]);

  // Merge the internal ref with any forwarded ref
  const mergedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      (
        scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>
      ).current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref, scrollContainerRef],
  );

  return (
    <div className="relative group/carousel">
      {canScrollLeft && (
        <div
          className="absolute left-0 top-0 bottom-0 w-24 pointer-events-none z-10"
          style={{
            background: `linear-gradient(to right, ${gradientColor}, transparent)`,
          }}
        />
      )}
      {canScrollRight && (
        <div
          className="absolute right-0 top-0 bottom-0 w-24 pointer-events-none z-10"
          style={{
            background: `linear-gradient(to left, ${gradientColor}, transparent)`,
          }}
        />
      )}

      <div
        ref={mergedRef}
        className={twMerge(
          carouselVariants({ gap, snap, hideScrollbar }),
          className,
          UNSAFE_className,
        )}
      >
        {children}
      </div>

      <Button
        icon
        variant="secondary"
        size="base"
        onClick={() => scroll("left")}
        UNSAFE_className={twMerge(
          navButtonVariants({ navButtonOffset, position: "left" }),
          !canScrollLeft && "md:hidden",
        )}
        aria-label="Scroll left"
      >
        <ChevronLeft className="w-4 h-4 text-muted" />
      </Button>
      <Button
        icon
        variant="secondary"
        size="base"
        onClick={() => scroll("right")}
        UNSAFE_className={twMerge(
          navButtonVariants({ navButtonOffset, position: "right" }),
          !canScrollRight && "md:hidden",
        )}
        aria-label="Scroll right"
      >
        <ChevronRight className="w-4 h-4 text-muted" />
      </Button>
    </div>
  );
}

// NOTE: Type cast to preserve generic type parameter for className prop
// See: https://fettblog.eu/typescript-react-generic-forward-refs/
const HorizontalCarousel = React.forwardRef(HorizontalCarouselInner) as <
  ClassName extends string,
>(
  props: HorizontalCarouselProps<ClassName> &
    React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

export { HorizontalCarousel };
